import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const PURPOSES = [
  ["small_business", "Start a small business"],
  ["expand_business", "Expand an existing business"],
  ["farming", "Farming / Agriculture"],
  ["skills", "Skills / Tools / Equipment"],
  ["education", "Education or vocational training"],
  ["women", "Women's empowerment"],
  ["youth", "Youth empowerment"],
  ["community", "Community livelihood activity"],
  ["other", "Other"],
];

const EMPTY = {
  full_name: "", date_of_birth: "", gender: "", phone: "", email: "",
  residential_address: "", state: "", lga: "", ward: "",
  occupation: "", employment_status: "unemployed", household_size: 1,
  id_type: "NIN", id_number: "", photo_url: "",
  purpose: "small_business", purpose_details: "",
};

export default function BeneficiaryRegister() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { document.title = "Register as a Beneficiary | Dutsen Kauna Foundation"; }, []);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, household_size: Number(form.household_size || 1) };
      const { data } = await api.post("/beneficiaries/register", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copySerial = async () => {
    if (!result?.serial) return;
    try { await navigator.clipboard.writeText(result.serial); toast.success("Serial copied"); } catch (e) { console.error("Clipboard unavailable", e); toast.error("Could not copy — please select the serial manually"); }
  };

  if (result) {
    return (
      <div data-testid="beneficiary-success" className="min-h-[70vh] bg-[#f8f4e8] py-20">
        <div className="dk-container max-w-2xl">
          <div className="bg-white rounded-2xl p-10 border border-[#d6aa43]/20 text-center">
            <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={48} />
            <h1 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mb-3">Congratulations, {result.full_name.split(" ")[0]}!</h1>
            <p className="text-[#0a0d12]/75 mb-6">Your registration has been received. Please keep this number safe — it will be used for programme selection and verification.</p>
            <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">Beneficiary serial number</div>
              <div className="font-mono text-3xl md:text-4xl font-bold text-[#d6aa43] mb-3" data-testid="beneficiary-serial">{result.serial}</div>
              <button onClick={copySerial} className="text-sm text-white hover:text-white inline-flex items-center gap-1"><Copy size={14} /> Copy</button>
            </div>
            <div className="text-left p-5 rounded-xl bg-[#f8f4e8] border border-[#d6aa43]/20 text-sm text-[#0a0d12]/75 mb-6">
              <Info className="inline text-[#d6aa43]" size={16} /> Registration does not guarantee that an applicant will receive an award. Verification is required before entering the selection pool.
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/empowerment" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Back to programme</Link>
              <Link to="/donate" className="dk-btn-primary">Support the programme</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="beneficiary-register-page">
      <section className="relative bg-[#155e3f] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Free beneficiary registration</span>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mt-3 max-w-3xl">Register in a few minutes. Zero fees. Ever.</h1>
          <p className="mt-4 text-white max-w-2xl">Complete the form below. You will receive a unique <strong className="text-white">DKF-BEN-2026-XXXXXX</strong> serial that becomes your permanent Foundation reference.</p>
        </div>
      </section>

      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container max-w-3xl">
          <form onSubmit={submit} className="bg-white rounded-2xl p-8 md:p-10 border border-black/5" data-testid="beneficiary-form">
            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mb-6">Personal information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required value={form.full_name} onChange={(v) => up("full_name", v)} testid="ben-full-name" />
              <Field label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => up("date_of_birth", v)} testid="ben-dob" />
              <Select label="Gender" value={form.gender} onChange={(v) => up("gender", v)} testid="ben-gender"
                options={[["", "Select"], ["male", "Male"], ["female", "Female"], ["other", "Other"]]} />
              <Field label="Phone number" required value={form.phone} onChange={(v) => up("phone", v)} placeholder="+234 806 000 0000" testid="ben-phone" />
              <Field label="Email" type="email" required value={form.email} onChange={(v) => up("email", v)} className="sm:col-span-2" testid="ben-email" />
              <Field label="Residential address" value={form.residential_address} onChange={(v) => up("residential_address", v)} className="sm:col-span-2" testid="ben-address" />
              <Field label="State" value={form.state} onChange={(v) => up("state", v)} testid="ben-state" />
              <Field label="Local Government Area" value={form.lga} onChange={(v) => up("lga", v)} testid="ben-lga" />
              <Field label="Ward / Community" value={form.ward} onChange={(v) => up("ward", v)} testid="ben-ward" />
              <Field label="Occupation" value={form.occupation} onChange={(v) => up("occupation", v)} testid="ben-occupation" />
              <Select label="Employment status" value={form.employment_status} onChange={(v) => up("employment_status", v)} testid="ben-employment"
                options={[["unemployed", "Unemployed"], ["self-employed", "Self-employed"], ["employed", "Employed"], ["student", "Student"], ["retired", "Retired"]]} />
              <Field label="Household size" type="number" value={form.household_size} onChange={(v) => up("household_size", v)} testid="ben-household" />
            </div>

            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mt-8 mb-6">Identification</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Select label="ID type" value={form.id_type} onChange={(v) => up("id_type", v)} testid="ben-id-type"
                options={[["NIN", "National ID (NIN)"], ["Voter", "Voter's card"], ["Driver", "Driver's licence"], ["Passport", "Passport"], ["Other", "Other"]]} />
              <Field label="ID number" value={form.id_number} onChange={(v) => up("id_number", v)} testid="ben-id-number" />
              <Field label="Photograph URL (optional)" value={form.photo_url} onChange={(v) => up("photo_url", v)} className="sm:col-span-2" placeholder="Link to your photo" testid="ben-photo" />
            </div>

            <h2 className="font-serif text-2xl text-[#0a0d12] font-bold mt-8 mb-6">Empowerment purpose</h2>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-2 block">Select what you would use the empowerment support for *</label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="purpose-options">
                {PURPOSES.map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => up("purpose", k)}
                    className={`p-3 text-left rounded-md border-2 text-sm font-semibold transition ${form.purpose === k ? "bg-[#155e3f] text-[#d6aa43] border-[#0d1724]" : "border-black/10 text-[#0a0d12] hover:border-[#d6aa43]"}`}
                    data-testid={`purpose-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">Short explanation</label>
              <textarea rows={4} value={form.purpose_details} onChange={(e) => up("purpose_details", e.target.value)} className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid="ben-purpose-details" />
            </div>

            <div className="mt-6 p-4 rounded-md bg-[#f8f4e8] border border-[#d6aa43]/20 text-sm text-[#0a0d12]/80 flex gap-2">
              <Info size={16} className="text-[#d6aa43] flex-shrink-0 mt-0.5" />
              Bank details are only collected at the verification/payment stage — never during registration. Registration is 100% free.
            </div>

            <button type="submit" disabled={loading} className="dk-btn-primary mt-6 w-full justify-center disabled:opacity-60" data-testid="ben-submit">
              {loading ? "Submitting…" : "Complete free registration"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "", testid, placeholder }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      <input required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={testid} />
    </div>
  );
}
function Select({ label, value, onChange, options, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-1 block">{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]" data-testid={testid}>
        {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
      </select>
    </div>
  );
}
